/**
 * Analytics Dashboard Page.
 *
 * Displays visitor analytics data including:
 * - Key engagement metrics (for ad sales)
 * - Hourly traffic chart
 * - Flow patterns breakdown (for product insights)
 * - Navigation flows and entry pages
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ReplayIcon from '@mui/icons-material/Replay';
import TimerIcon from '@mui/icons-material/Timer';

import { analyticsApi } from '@/api/client';

/**
 * Stat card component for displaying key metrics.
 */
function StatCard({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
}) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            display: 'flex',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={600}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Format seconds into human-readable duration.
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/**
 * Simple bar chart for hourly traffic.
 */
function HourlyChart({ data }: { data: Array<{ hour: string; count: number }> }) {
  if (!data || data.length === 0) {
    return (
      <Typography color="text.secondary" align="center">
        No data available
      </Typography>
    );
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 100 }}>
      {data.map((item, index) => {
        const height = (item.count / maxCount) * 100;
        const hour = new Date(item.hour).getHours();
        return (
          <Box
            key={index}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: `${Math.max(height, 2)}%`,
                bgcolor: 'primary.main',
                borderRadius: '2px 2px 0 0',
                minHeight: 2,
              }}
              title={`${hour}:00 - ${item.count} sessions`}
            />
            {data.length <= 24 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {hour}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState(24);
  const [routesExpanded, setRoutesExpanded] = useState(false);

  // Fetch analytics data
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ['analytics-summary', timeRange],
    queryFn: () => analyticsApi.summary(timeRange),
    refetchInterval: 30000,
  });

  const { data: patterns, isLoading: patternsLoading } = useQuery({
    queryKey: ['analytics-patterns', timeRange],
    queryFn: () => analyticsApi.patterns(timeRange, 10),
    refetchInterval: 30000,
  });

  const { data: pages, isLoading: pagesLoading } = useQuery({
    queryKey: ['analytics-pages', timeRange],
    queryFn: () => analyticsApi.pages(timeRange, 15),
    refetchInterval: 30000,
  });

  const { data: flows, isLoading: flowsLoading } = useQuery({
    queryKey: ['analytics-flows', timeRange],
    queryFn: () => analyticsApi.flows(timeRange, 10),
    refetchInterval: 30000,
  });

  // Only fetch routes when accordion is expanded (lazy load)
  const { data: routes, isLoading: routesLoading } = useQuery({
    queryKey: ['analytics-routes', timeRange],
    queryFn: () => analyticsApi.routes(timeRange, 100),
    enabled: routesExpanded, // Only fetch when expanded
    refetchInterval: routesExpanded ? 30000 : false,
  });

  const isLoading = summaryLoading || patternsLoading || pagesLoading || flowsLoading;

  if (summaryError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load analytics data. Make sure the analytics worker is running.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visitor engagement and flow analysis
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value as number)}
          >
            <MenuItem value={1}>Last hour</MenuItem>
            <MenuItem value={6}>Last 6 hours</MenuItem>
            <MenuItem value={12}>Last 12 hours</MenuItem>
            <MenuItem value={24}>Last 24 hours</MenuItem>
            <MenuItem value={48}>Last 48 hours</MenuItem>
            <MenuItem value={168}>Last 7 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading && !summary ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ============================================================ */}
          {/* TOP SECTION: Key Metrics (for Ad Sales) */}
          {/* ============================================================ */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Key Metrics
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Sessions"
                value={summary?.total_sessions ?? 0}
                icon={<AccessTimeIcon />}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Unique Visitors"
                value={summary?.unique_visitors ?? 0}
                icon={<PeopleIcon />}
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Page Views"
                value={summary?.total_page_views ?? 0}
                icon={<VisibilityIcon />}
                color="info"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Bounce Rate"
                value={`${summary?.bounce_rate ?? 0}%`}
                icon={<ExitToAppIcon />}
                color={summary?.bounce_rate && summary.bounce_rate > 50 ? 'warning' : 'success'}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Return Visitors"
                value={summary?.return_visitors ?? 0}
                icon={<ReplayIcon />}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                title="Avg Duration"
                value={formatDuration(summary?.avg_duration_seconds ?? 0)}
                icon={<TimerIcon />}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Hourly Traffic Chart */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Traffic by Hour
            </Typography>
            <HourlyChart data={summary?.sessions_by_hour ?? []} />
          </Paper>

          <Divider sx={{ my: 3 }} />

          {/* ============================================================ */}
          {/* MIDDLE SECTION: Flow Patterns (for Product Insights) */}
          {/* ============================================================ */}
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            User Flow Patterns
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Flow Patterns */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Common Journeys
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  How users navigate through the app
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {patterns?.patterns.map((pattern, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {pattern.pattern}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pattern.count} ({pattern.percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pattern.percentage}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                  {(!patterns?.patterns || patterns.patterns.length === 0) && (
                    <Typography color="text.secondary">No pattern data available</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Most Visited Pages */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Most Visited Pages
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Page</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pages?.pages.map((page, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {page.page_path}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{page.visits}</TableCell>
                          <TableCell align="right">{page.unique_sessions}</TableCell>
                        </TableRow>
                      ))}
                      {(!pages?.pages || pages.pages.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary">No data available</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Navigation Flows & Entry Pages */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Page-to-Page Flows
                </Typography>
                <TableContainer sx={{ maxHeight: 250 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>From → To</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flows?.flows.map((flow, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {flow.referrer}
                              </Typography>
                              <Typography color="text.secondary">→</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {flow.page_path}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{flow.count}</TableCell>
                        </TableRow>
                      ))}
                      {(!flows?.flows || flows.flows.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            <Typography color="text.secondary">No data available</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Entry Pages
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Where users land first (from external links)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {flows?.entry_pages.map((entry, index) => (
                    <Chip
                      key={index}
                      label={`${entry.page_path} (${entry.count})`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                  {(!flows?.entry_pages || flows.entry_pages.length === 0) && (
                    <Typography color="text.secondary">No data available</Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ============================================================ */}
          {/* BOTTOM SECTION: Detailed Data (Collapsed) */}
          {/* ============================================================ */}
          <Accordion
            expanded={routesExpanded}
            onChange={(_, expanded) => setRoutesExpanded(expanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="action" />
                <Typography variant="h6" fontWeight={600}>
                  Detailed Routes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (Exact paths with IDs - loads on expand)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {routesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Exact Path</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {routes?.routes.map((route, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {route.page_path}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{route.visits}</TableCell>
                          <TableCell align="right">{route.unique_sessions}</TableCell>
                        </TableRow>
                      ))}
                      {(!routes?.routes || routes.routes.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary">No data available</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
